<template>
  <div class="home">
    <div class="home-header">
      <h1>Cuxa Anastylose</h1>
      <p class="subtitle">Plateforme d'exploration d'hypothèses d'anastylose 2D</p>
    </div>

    <div class="home-content">
      <div class="home-tabs">
        <button
          type="button"
          class="home-tab"
          :class="{ active: homeTab === 'hypotheses' }"
          @click="setHomeTab('hypotheses')"
        >
          Hypothèses
        </button>
        <button
          type="button"
          class="home-tab"
          :class="{ active: homeTab === 'blocs' }"
          @click="setHomeTab('blocs')"
        >
          Blocs
        </button>
      </div>

      <template v-if="homeTab === 'hypotheses'">
      <div class="home-actions">
        <button @click="showCreateModal = true" class="btn-primary">
          + Nouvelle hypothèse
        </button>
        <button
          v-if="lastHypothesis"
          @click="goToWorkspace(lastHypothesis.id)"
          class="btn-secondary"
        >
          Continuer dernière hypothèse
        </button>
      </div>

      <div v-if="loading" class="loading-state">
        <div class="spinner"></div>
        <p>Chargement...</p>
      </div>

      <div v-else-if="error" class="error-state">
        <p>{{ error }}</p>
        <button @click="loadHypotheses" class="btn-retry">Réessayer</button>
      </div>

      <div v-else-if="hypotheses.length === 0" class="empty-state">
        <p>Aucune hypothèse pour le moment</p>
        <button @click="showCreateModal = true" class="btn-primary">
          Créer votre première hypothèse
        </button>
      </div>

      <div v-else class="hypotheses-grid">
        <div
          v-for="hypothesis in hypotheses"
          :key="hypothesis.id"
          class="hypothesis-card"
          @click="goToWorkspace(hypothesis.id)"
        >
          <div class="card-header">
            <h3>{{ hypothesis.name }}</h3>
            <div class="card-actions" @click.stop>
              <button
                @click.stop="editHypothesis(hypothesis)"
                class="btn-icon"
                title="Modifier"
              >
                <span class="material-symbols-outlined">edit</span>
              </button>
              <button
                @click.stop="duplicateHypothesis(hypothesis)"
                class="btn-icon"
                title="Dupliquer"
              >
                <span class="material-symbols-outlined">content_copy</span>
              </button>
              <button
                @click.stop="deleteHypothesis(hypothesis)"
                class="btn-icon btn-danger"
                title="Supprimer"
              >
                <span class="material-symbols-outlined">delete</span>
              </button>
            </div>
          </div>
          <p v-if="hypothesis.comment" class="card-comment">
            {{ hypothesis.comment }}
          </p>
          <div class="card-meta">
            <span class="card-date" :title="'Créé le ' + formatDateFull(hypothesis.created_at)">
              {{ formatDate(hypothesis.created_at) }}
            </span>
            <span v-if="hypothesis.blocks_count != null" class="card-blocks-count">
              {{ hypothesis.blocks_count }} bloc{{ hypothesis.blocks_count !== 1 ? 's' : '' }}
            </span>
          </div>
          <div class="card-footer">
            <span v-if="hypothesis.updated_at !== hypothesis.created_at" class="card-updated">
              Modifié {{ formatDate(hypothesis.updated_at) }}
            </span>
          </div>
        </div>
      </div>
      </template>

      <template v-else>
        <div class="blocs-toolbar">
          <input
            v-model="blocsSearch"
            type="text"
            class="blocs-search"
            placeholder="Rechercher un bloc..."
          />
        </div>
        <div v-if="blocsLoading" class="loading-state">
          <div class="spinner"></div>
          <p>Chargement...</p>
        </div>
        <div v-else-if="blocsError" class="error-state">
          <p>{{ blocsError }}</p>
          <button type="button" class="btn-retry" @click="loadBlocs">Réessayer</button>
        </div>
        <div v-else-if="explorerBlocs.length === 0" class="empty-state">
          <p>Aucun bloc pour le moment</p>
        </div>
        <div v-else class="blocs-explorer-grid">
          <BlockCard
            v-for="bloc in explorerBlocs"
            :key="bloc.id"
            :bloc="bloc"
            :selected="false"
            :enable-drag="false"
            :search-highlight="blocsSearch.trim() || undefined"
            @select="openBlocFromExplorer"
          />
        </div>
      </template>
    </div>

    <HypothesisModal
      :show="showCreateModal || showEditModal"
      :hypothesis="editingHypothesis"
      @close="closeModal"
      @created="handleCreated"
      @updated="handleUpdated"
    />

    <ConfirmModal
      :show="showDeleteConfirm"
      title="Supprimer l'hypothèse"
      :message="deleteConfirmMessage"
      confirm-label="Supprimer"
      :danger="true"
      @confirm="confirmDeleteHypothesis"
      @cancel="showDeleteConfirm = false; hypothesisToDelete = null"
    />

    <footer class="home-footer">
      <div class="footer-content">
        <p class="footer-text">Cuxa Anastylose v{{ appVersion }}</p>
        <button @click="goToChangelog" class="btn-changelog">
          Historique des versions
        </button>
      </div>
    </footer>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useHypothesesStore } from '@stores/hypotheses.store';
import { useBlocsStore } from '@stores/blocs.store';
import { useToast } from '../composables/useToast';
import HypothesisModal from '../components/HypothesisModal.vue';
import ConfirmModal from '../components/ConfirmModal.vue';
import BlockCard from '../components/BlockCard.vue';
import type { Hypothesis } from '@api/hypotheses.api';
import type { Bloc } from '@api/blocs.api';
import changelogData from '@/assets/CHANGELOG.json';

const appVersion = computed(() => {
  const versions = (changelogData as { versions?: { version: string }[] }).versions;
  return versions?.length ? versions[0].version : '0.0.0';
});

const route = useRoute();
const router = useRouter();
const hypothesesStore = useHypothesesStore();
const blocsStore = useBlocsStore();
const { success, error: showError } = useToast();

type HomeTab = 'hypotheses' | 'blocs';

const homeTab = computed<HomeTab>(() =>
  route.query.tab === 'blocs' ? 'blocs' : 'hypotheses'
);

const setHomeTab = (tab: HomeTab) => {
  const query = { ...route.query };
  if (tab === 'blocs') {
    query.tab = 'blocs';
  } else {
    delete query.tab;
  }
  router.replace({ query });
};

const blocsSearch = ref('');
let blocsSearchDebounce: ReturnType<typeof setTimeout> | null = null;

const showCreateModal = ref(false);
const showEditModal = ref(false);
const editingHypothesis = ref<Hypothesis | null>(null);
const showDeleteConfirm = ref(false);
const hypothesisToDelete = ref<Hypothesis | null>(null);

const loading = computed(() => hypothesesStore.loading);
const error = computed(() => hypothesesStore.error);
const hypotheses = computed(() => hypothesesStore.hypotheses);

const lastHypothesis = computed(() => {
  if (hypotheses.value.length === 0) return null;
  return [...hypotheses.value].sort(
    (a, b) =>
      new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
  )[0];
});

const blocsLoading = computed(() => blocsStore.loading);
const blocsError = computed(() => blocsStore.error);
const explorerBlocs = computed(() => blocsStore.blocs);

const loadBlocs = async () => {
  await blocsStore.fetchAll(blocsSearch.value.trim() || undefined);
};

watch(homeTab, (tab) => {
  if (tab === 'blocs') {
    loadBlocs();
  }
}, { immediate: true });

watch(blocsSearch, () => {
  if (homeTab.value !== 'blocs') return;
  if (blocsSearchDebounce) clearTimeout(blocsSearchDebounce);
  blocsSearchDebounce = setTimeout(() => {
    loadBlocs();
    blocsSearchDebounce = null;
  }, 300);
});

const openBlocFromExplorer = (bloc: Bloc) => {
  if (!bloc.composite_slug) return;
  router.push({ name: 'bloc-composite', params: { id: String(bloc.id) } });
};

const loadHypotheses = async () => {
  await hypothesesStore.fetchAll();
};

const goToWorkspace = (hypothesisId?: number) => {
  if (hypothesisId) {
    router.push(`/workspace/${hypothesisId}`);
  } else {
    router.push('/workspace');
  }
};

const editHypothesis = (hypothesis: Hypothesis) => {
  editingHypothesis.value = hypothesis;
  showEditModal.value = true;
};

const duplicateHypothesis = async (hypothesis: Hypothesis) => {
  try {
    const created = await hypothesesStore.duplicate(hypothesis.id);
    success('Hypothèse dupliquée');
    goToWorkspace(created.id);
  } catch (err: any) {
    showError(err.message || 'Erreur lors de la duplication');
  }
};

const deleteConfirmMessage = computed(() =>
  hypothesisToDelete.value
    ? `Êtes-vous sûr de vouloir supprimer l'hypothèse « ${hypothesisToDelete.value.name } » ? Cette action est irréversible.`
    : ''
);

const deleteHypothesis = (hypothesis: Hypothesis) => {
  hypothesisToDelete.value = hypothesis;
  showDeleteConfirm.value = true;
};

const confirmDeleteHypothesis = async () => {
  if (!hypothesisToDelete.value) return;
  const hyp = hypothesisToDelete.value;
  hypothesisToDelete.value = null;
  showDeleteConfirm.value = false;
  try {
    await hypothesesStore.delete(hyp.id);
    success('Hypothèse supprimée avec succès');
  } catch (err: any) {
    showError(err.message || 'Erreur lors de la suppression');
  }
};

const closeModal = () => {
  showCreateModal.value = false;
  showEditModal.value = false;
  editingHypothesis.value = null;
};

const handleCreated = (hypothesis: Hypothesis) => {
  goToWorkspace(hypothesis.id);
};

const handleUpdated = () => {
  loadHypotheses();
};

// Normalise une chaîne date (null/undefined ou format MySQL "YYYY-MM-DD HH:mm:ss") pour parsing fiable
const parseDate = (dateString: string | null | undefined): Date | null => {
  if (dateString == null || String(dateString).trim() === '') return null;
  const s = String(dateString).trim();
  const iso = s.includes('T') ? s : s.replace(' ', 'T') + (s.match(/\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}/) && !s.endsWith('Z') ? 'Z' : '');
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatDate = (dateString: string | null | undefined) => {
  const date = parseDate(dateString);
  if (!date) return 'Date inconnue';
  return date.toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

const formatDateFull = (dateString: string | null | undefined) => {
  const date = parseDate(dateString);
  if (!date) return 'Date inconnue';
  return date.toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const goToChangelog = () => {
  router.push('/changelog');
};

onMounted(() => {
  loadHypotheses();
});
</script>

<style scoped>
.home {
  min-height: 100vh;
  background-color: #1a1a1a;
  color: #e0e0e0;
  padding: 2rem;
  position: relative;
}

.home-header {
  text-align: center;
  margin-bottom: 3rem;
  padding: 4rem 2rem;
  background-image: url('@/assets/cuxa-hero-section.png');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  border-radius: 12px;
  border: 1px solid rgba(74, 158, 255, 0.3);
  position: relative;
  overflow: hidden;
}

.home-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(26, 26, 26, 0.7) 0%, rgba(26, 26, 26, 0.5) 100%);
  z-index: 1;
}

.home-header h1 {
  font-size: 3rem;
  margin-bottom: 0.5rem;
  color: #ffffff;
  font-weight: 700;
  letter-spacing: -0.5px;
  position: relative;
  z-index: 2;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.5);
}

.subtitle {
  color: #e0e0e0;
  font-size: 1.2rem;
  font-weight: 300;
  position: relative;
  z-index: 2;
  text-shadow: 0 1px 5px rgba(0, 0, 0, 0.5);
}

.home-content {
  max-width: 1200px;
  margin: 0 auto 2.5rem;
}

.home-tabs {
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  margin-bottom: 2rem;
}

.home-tab {
  padding: 0.6rem 1.5rem;
  background: none;
  border: 1px solid #444;
  border-radius: 4px;
  color: #999;
  cursor: pointer;
  font-size: 1rem;
}

.home-tab:hover {
  border-color: #4a9eff;
  color: #e0e0e0;
}

.home-tab.active {
  background-color: #4a9eff;
  border-color: #4a9eff;
  color: #fff;
}

.blocs-toolbar {
  display: flex;
  justify-content: center;
  margin-bottom: 1.5rem;
}

.blocs-search {
  width: 100%;
  max-width: 420px;
  padding: 0.6rem 0.9rem;
  background-color: #2a2a2a;
  border: 1px solid #444;
  border-radius: 4px;
  color: #e0e0e0;
  font-size: 1rem;
}

.blocs-search:focus {
  outline: none;
  border-color: #4a9eff;
}

.blocs-explorer-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 1rem;
}

.home-actions {
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin-bottom: 3rem;
}

.btn-primary,
.btn-secondary {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 1rem;
  font-weight: 500;
  transition: all 0.2s;
}

.btn-primary {
  background-color: #4a9eff;
  color: white;
}

.btn-primary:hover {
  background-color: #3a8eef;
}

.btn-secondary {
  background-color: #555;
  color: #e0e0e0;
}

.btn-secondary:hover {
  background-color: #666;
}

.loading-state,
.error-state,
.empty-state {
  text-align: center;
  padding: 3rem;
  color: #999;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #444;
  border-top-color: #4a9eff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin: 0 auto 1rem;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.error-state {
  color: #dc3545;
}

.btn-retry {
  margin-top: 1rem;
  padding: 0.5rem 1rem;
  background-color: #555;
  color: #e0e0e0;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.hypotheses-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
}

.hypothesis-card {
  background-color: #2a2a2a;
  border: 1px solid #444;
  border-radius: 8px;
  padding: 1.5rem;
  cursor: pointer;
  transition: all 0.2s;
}

.hypothesis-card:hover {
  border-color: #4a9eff;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(74, 158, 255, 0.2);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0.75rem;
}

.card-header h3 {
  margin: 0;
  font-size: 1.25rem;
  color: #e0e0e0;
  flex: 1;
}

.card-actions {
  display: flex;
  gap: 0.35rem;
  padding: 0.2rem;
  border: 1px solid #3a3a3a;
  border-radius: 999px;
  background: rgba(15, 15, 15, 0.45);
  backdrop-filter: blur(2px);
}

.btn-icon {
  background: transparent;
  border: 1px solid transparent;
  color: #aab4bf;
  cursor: pointer;
  padding: 0.25rem;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  transition: all 0.2s;
}

.btn-icon .material-symbols-outlined {
  font-size: 18px;
}

.btn-icon:hover {
  background-color: rgba(74, 158, 255, 0.16);
  border-color: rgba(74, 158, 255, 0.35);
  color: #d8e9ff;
  transform: translateY(-1px);
}

.btn-icon:focus-visible {
  outline: none;
  border-color: #4a9eff;
  box-shadow: 0 0 0 2px rgba(74, 158, 255, 0.25);
}

.btn-danger {
  color: #ff7b88;
}

.btn-danger:hover {
  background-color: #dc3545;
  border-color: #dc3545;
  color: #111111;
}

.card-comment {
  color: #999;
  font-size: 0.9rem;
  margin-bottom: 1rem;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.card-meta {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 0.8rem;
  color: #666;
  margin-bottom: 0.5rem;
}

.card-blocks-count {
  color: #4a9eff;
  font-weight: 500;
}

.card-footer {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  font-size: 0.8rem;
  color: #666;
  padding-top: 0.75rem;
  border-top: 1px solid #333;
}

.card-date,
.card-updated {
  font-size: 0.8rem;
}

.home-footer {
  background-color: rgba(26, 26, 26, 0.95);
  border-top: 1px solid #333;
  box-sizing: border-box;
  max-width: 1200px;
  width: 100%;
  margin-inline: auto;
  height: 40px;
  padding: 0 16px;
  display: flex;
  align-items: center;
}

.footer-content {
  width: 100%;
  min-width: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
}

.footer-text {
  color: #666;
  font-size: 12px;
  line-height: 1;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.btn-changelog {
  flex-shrink: 0;
  padding: 4px 10px;
  background-color: #333;
  border: 1px solid #444;
  border-radius: 4px;
  color: #e0e0e0;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.2s;
}

@media (max-width: 480px) {
  .home-footer {
    height: auto;
    min-height: 40px;
    padding: 8px 12px;
  }

  .footer-content {
    flex-wrap: wrap;
  }

  .footer-text {
    white-space: normal;
  }
}

.btn-changelog:hover {
  background-color: #444;
  border-color: #4a9eff;
  color: #4a9eff;
}
</style>
